import { useCallback, useEffect, useState } from 'react';
import { isNativeApp } from './native';
import {
  disableNativePush,
  enableNativePush,
  nativePermission,
  storedNativeToken,
} from './native-push';
import { useSession } from './session';

/**
 * De weg van "meldingen aanzetten" naar een abonnement op de server.
 *
 * Vier stappen die elk apart kunnen mislukken, en dat is de reden dat dit een eigen bestand is:
 * een service worker registreren, de publieke sleutel ophalen, de browser om toestemming
 * vragen, en het abonnement aanmelden. Elke stap heeft zijn eigen uitleg nodig, want een
 * medewerker die "toestaan" heeft weggeklikt, kan dat niet ongedaan maken zonder in de
 * instellingen van zijn browser te duiken.
 */

export type PushState =
  | 'unsupported' // deze browser kan geen web push (of iOS zonder app op het beginscherm)
  | 'ios-install-needed' // Safari op iOS: eerst op het beginscherm zetten
  | 'denied' // de gebruiker heeft eerder geweigerd
  | 'off' // kan aan, staat uit
  | 'on'
  | 'busy';

/** Staat deze pagina in een app die op het beginscherm is gezet? */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari op iOS heeft zijn eigen vlag, en die is het enige signaal dat daar werkt.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/** Een naam die een medewerker terugkent in zijn lijst met toestellen. */
function deviceLabel(): string {
  const agent = window.navigator.userAgent;
  if (/iphone/i.test(agent)) return 'iPhone';
  if (/ipad/i.test(agent)) return 'iPad';
  if (/android/i.test(agent)) return 'Androidtelefoon';
  if (/mac os/i.test(agent)) return 'Mac';
  if (/windows/i.test(agent)) return 'Windows-computer';
  return 'Dit toestel';
}

export function usePush() {
  const { api } = useSession();
  const [state, setState] = useState<PushState>('busy');

  const detect = useCallback(async () => {
    // In de native app gaat het over de toestemming van het toestel, niet over de browser (MOB-1).
    if (isNativeApp()) {
      const permission = await nativePermission();
      if (permission === 'denied') setState('denied');
      else setState(permission === 'granted' && storedNativeToken() ? 'on' : 'off');
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      // Op iOS bestaat `PushManager` alleen in een app die op het beginscherm staat. Dat is
      // geen fout maar een instructie, en die krijgt een eigen toestand.
      setState(isIos() && !isStandalone() ? 'ios-install-needed' : 'unsupported');
      return;
    }

    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    const existing = await registration?.pushManager.getSubscription();
    setState(existing ? 'on' : 'off');
  }, []);

  useEffect(() => {
    void detect();
  }, [detect]);

  /** Meldt dit abonnement aan bij de server en geeft het id ervan terug. */
  const register = useCallback(
    async (subscription: globalThis.PushSubscription): Promise<string | null> => {
      const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
      const { data } = await api.POST('/api/v1/push/subscriptions', {
        body: {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
          deviceLabel: deviceLabel(),
        } as never,
      });
      return (data as { id?: string } | undefined)?.id ?? null;
    },
    [api],
  );

  /**
   * Zet meldingen aan.
   *
   * De uitleg staat vóór deze aanroep op het scherm, niet erin: een browserprompt die uit de
   * lucht komt vallen, wordt geweigerd — en een geweigerde prompt komt niet terug.
   */
  const enable = useCallback(async (): Promise<PushState> => {
    setState('busy');

    if (isNativeApp()) {
      try {
        const permission = await enableNativePush(api);
        const next: PushState =
          permission === 'granted' ? 'on' : permission === 'denied' ? 'denied' : 'off';
        setState(next);
        return next;
      } catch {
        await detect();
        return 'off';
      }
    }

    try {
      // De worker is al aangemeld bij het starten van de app (Stap 12): hij doet ook het offline
      // werk en moet bestaan vóórdat iemand ergens op tikt. Hier alleen wachten tot hij er is.
      const registration = await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return permission === 'denied' ? 'denied' : 'off';
      }

      const { data } = await api.GET('/api/v1/push/vapid-public-key');
      const publicKey = (data as { publicKey?: string } | undefined)?.publicKey;
      if (!publicKey) {
        setState('unsupported');
        return 'unsupported';
      }

      const subscription = await registration.pushManager.subscribe({
        // Verplicht bij Chrome: elke melding moet iets tonen. Dat past bij ons ontwerp —
        // stille meldingen zijn precies wat een werkgever niet hoort te kunnen versturen.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      await register(subscription);

      setState('on');
      return 'on';
    } catch {
      await detect();
      return 'off';
    }
  }, [api, detect, register]);

  /**
   * Zet meldingen uit op dít toestel; andere toestellen blijven staan.
   *
   * Eerst aanmelden en dan afmelden, en dat is geen omweg: aanmelden is idempotent op het adres
   * van de pushdienst, dus het geeft het id van precies dit toestel terug. Zoeken op de naam van
   * het toestel zou de verkeerde treffen bij iemand met twee dezelfde telefoons.
   */
  const disable = useCallback(async (): Promise<void> => {
    setState('busy');
    if (isNativeApp()) {
      await disableNativePush(api);
      await detect();
      return;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        const id = await register(subscription);
        await subscription.unsubscribe();
        if (id) {
          await api.DELETE('/api/v1/push/subscriptions/{subscriptionId}', {
            params: { path: { subscriptionId: id } },
          });
        }
      }
    } finally {
      await detect();
    }
  }, [api, detect, register]);

  return {
    state,
    enable,
    disable,
    refresh: detect,
    // De native app is al geïnstalleerd; de uitleg over het beginscherm geldt daar niet.
    iosInstallNeeded: !isNativeApp() && isIos() && !isStandalone(),
  };
}

/**
 * De VAPID-sleutel van base64url naar bytes.
 *
 * De browser wil een `Uint8Array`; de server geeft de sleutel als tekst. Deze omzetting staat
 * in elk web-pushvoorbeeld op het internet, en dat is precies waarom hij hier met uitleg staat:
 * het is de enige plek waar een stille fout een abonnement oplevert dat nooit iets ontvangt.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  // Een eigen `ArrayBuffer` en niet de standaard: `applicationServerKey` wil er een die niet
  // gedeeld kan zijn, en sinds TypeScript 5.7 is dat een verschil dat de typering ziet.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
  return output;
}
